"use client";

import Link from "next/link";
import { Check } from "lucide-react";
import { PLANS as CATALOGUE } from "@/constants/subscription-plans";
import { useEstDansAppIOS } from "@/hooks/useEstDansAppIOS";

interface Plan {
  name: string;
  /** Montant affiché, ou « Sur devis » pour les offres business (A1 + A2+). */
  price: string;
  period?: string;
  yearly?: string;
  tagline: string;
  features: string[];
  cta: string;
  href: string;
  popular?: boolean;
  /**
   * Cette carte mène-t-elle à un abonnement PAYANT ?
   *
   * Sert uniquement à l'application iOS (règle App Store 3.1.1, voir
   * `useEstDansAppIOS`). Free est gratuit et les offres business passent par un
   * devis commercial : ni l'un ni l'autre n'est un mécanisme d'achat, seules
   * Starter et Pro en sont un.
   */
  payant?: boolean;
}

const PLANS: Plan[] = [
  {
    name: "Free",
    price: "0 €",
    tagline: "Consulter un projet qu'on vous partage.",
    features: [
      "Consultation de projets partagés",
      "Lecture seule — aucune modification",
      "Export (filigrane Synoplix)",
      "1 licence",
    ],
    cta: "Créer mon compte gratuit",
    href: "/register",
  },
  {
    name: "Starter",
    price: "15 € HT",
    period: "/ mois",
    // CGV-CGU v1.6, art. 13 : HT et TTC (TVA française 20 %).
    yearly: "180 € HT/an (216 € TTC), engagement 1 an\nou 18 € HT/mois (21,60 € TTC) sans engagement",
    tagline: "Pour les indépendants, sur le terrain.",
    features: [
      "Édition : charges & longueurs de câbles",
      "Petites distributions (PC16, 16 mono, P17)",
      "Plugin Vectorworks",
      "Export PDF/PNG/SVG/JSON/CSV",
      "25 symboles personnalisés",
      "Support par ticket (réponse sous 48 h ouvrées)",
    ],
    cta: "Choisir Starter",
    href: "/register",
    payant: true,
  },
  {
    name: "Pro",
    price: "75 € HT",
    period: "/ mois",
    yearly: "900 € HT/an (1 080 € TTC), engagement 1 an\nou 90 € HT/mois (108 € TTC) sans engagement",
    tagline: "La référence des bureaux d'études.",
    features: [
      "5 projets / mois · 10 collaborateurs",
      "Calculs normatifs NF C 15-100 (45 pays)",
      "Import DXF (fond de plan)",
      "Gestion des rôles & partage",
      // CGV-CGU v1.6, art. 17.
      "Support prioritaire et téléphone (24 h ouvrées)",
    ],
    cta: "Choisir Pro",
    href: "/register",
    payant: true,
    popular: true,
  },
];

/**
 * Objet du mail de contact — DÉRIVÉ du catalogue, pas saisi.
 *
 * Le comparatif de la même page construit déjà `Offre ${c.name}`
 * (PricingComparison.tsx). Écrire « Offre PME » ici donnerait deux objets
 * différents pour la MÊME offre selon le bouton cliqué, et casserait tout tri
 * de la boîte contact. Le libellé affiché reste français ; l'objet, lui, suit
 * le catalogue.
 */
function sujetDevis(plan: "SMB" | "ENTERPRISE") {
  return `mailto:contact@synoplix.com?subject=${encodeURIComponent(`Offre ${CATALOGUE[plan].name}`)}`;
}

/**
 * Offres business — MÊME carte que les offres individuelles, sans tarif.
 *
 * Le prix de SMB et Enterprise se négocie (frais de souscription A1 +
 * abonnement annuel A2+) : la carte affiche « Sur devis » à l'emplacement du
 * montant plutôt qu'un chiffre, exactement comme la colonne correspondante du
 * comparatif. Les avantages listés ici suivent `PLANS.SMB` / `PLANS.ENTERPRISE`
 * dans `src/constants/subscription-plans.ts` — ne pas les enrichir sans changer
 * la licence, sinon la carte promet ce que le comparatif refuse.
 */
const BUSINESS_PLANS: Plan[] = [
  {
    name: "PME",
    price: "Sur devis",
    yearly: "Setup Fee + Maintenance annuelle",
    tagline: "Déploiement d'équipe, onboarding dédié.",
    features: [
      "50 projets / mois · 25 collaborateurs",
      "Bibliothèques partagées d'entreprise",
      "Cartouche personnalisé (white-label)",
      "Journal d'audit & SLA contractuel",
      "Journée de prise en main · support 7 j/7 sous 24 h",
    ],
    cta: "Nous contacter",
    href: sujetDevis("SMB"),
  },
  {
    name: "Entreprise",
    price: "Sur devis",
    yearly: "Setup Fee + Maintenance annuelle",
    tagline: "Grands comptes — SSO, audit, support dédié.",
    features: [
      "Projets, pages et collaborateurs illimités",
      "Multi-équipe (plusieurs organisations)",
      "SSO (authentification unique)",
      "Journal d'audit & SLA contractuel",
      "Interlocuteur nommé · support 7 j/7 sous 12 h",
    ],
    cta: "Nous contacter",
    href: sujetDevis("ENTERPRISE"),
  },
];

function PlanCard({ plan }: { plan: Plan }) {
  const dansAppIOS = useEstDansAppIOS();
  // Dans l'app iOS, un chemin vers un abonnement payant devient une mention.
  // On garde la carte entière — prix et avantages compris : c'est de
  // l'information, et c'est elle qui rend la page utile.
  const masquerAchat = dansAppIOS && plan.payant === true;

  return (
    <div
      className={`relative flex flex-col border p-7 ${
        plan.popular
          ? "border-[#FF7A00] bg-white/[0.05] ring-1 ring-[#FF7A00]"
          : "border-white/10 bg-white/[0.03]"
      }`}
    >
      {plan.popular && (
        <span className="absolute -top-3 left-7 rounded-full bg-[#FF7A00] px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-[#0A1F44]">
          Populaire
        </span>
      )}
      <h3 className="text-lg font-bold text-white">{plan.name}</h3>
      <p className="mt-1 text-sm text-white/50">{plan.tagline}</p>
      <div className="mt-5 flex items-baseline gap-1">
        <span className="text-4xl font-extrabold text-white">{plan.price}</span>
        {plan.period && <span className="text-sm font-medium text-white/60">{plan.period}</span>}
      </div>
      <p className="mt-1 min-h-4 whitespace-pre-line text-xs leading-snug text-white/60">{plan.yearly ?? ""}</p>

      <ul className="mt-6 space-y-3">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-white/75">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#FF7A00]" />
            {f}
          </li>
        ))}
      </ul>

      {masquerAchat ? (
        <p className="mt-8 px-4 py-2.5 text-center text-xs text-white/50">
          Souscription sur synoplix.com
        </p>
      ) : (
        <Link
          href={plan.href}
          className={`mt-8 rounded-none px-4 py-2.5 text-center text-sm font-semibold transition-colors ${
            plan.popular
              ? "bg-[#FF7A00] text-[#0A1F44] hover:bg-[#FF7A00]/90"
              : "bg-white/10 text-white hover:bg-white/15"
          }`}
        >
          {plan.cta}
        </Link>
      )}
    </div>
  );
}

export function PricingCards() {
  return (
    <>
      <div className="mx-auto grid max-w-5xl gap-6 lg:grid-cols-3">
        {PLANS.map((p) => (
          <PlanCard key={p.name} plan={p} />
        ))}
      </div>

      {/* Offres business (SMB + Enterprise) — mêmes cartes, prix sur devis */}
      <div className="mx-auto mt-14 flex max-w-5xl items-center gap-4 lg:max-w-2xl">
        <span className="h-px flex-1 bg-white/10" />
        <span className="text-xs font-semibold uppercase tracking-wide text-white/55">
          Offres entreprise
        </span>
        <span className="h-px flex-1 bg-white/10" />
      </div>
      <div className="mx-auto mt-8 grid max-w-5xl gap-6 lg:max-w-2xl lg:grid-cols-2">
        {BUSINESS_PLANS.map((p) => (
          <PlanCard key={p.name} plan={p} />
        ))}
      </div>
    </>
  );
}
